import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { SectionIcon } from '../../components/TrainerUI';
import {
  ScheduleCardGrid, MonthGrid, RosterModal, ViewModeToggle,
  getRangeForMode, shiftAnchor, scheduleColumns, ymd,
} from '../../components/WeeklyScheduleGrid';
import { fmtDate } from '../../utils/format';

function rangeLabel(mode, anchorDate, range) {
  if (mode === 'day') return anchorDate.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  if (mode === 'month') return anchorDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  return `Week of ${fmtDate(range.monday)} – ${fmtDate(range.sunday)}`;
}

const TODAY_LABEL = { day: 'Today', week: 'This week', month: 'This month' };

export default function TrainerSchedulesPage() {
  const [mode, setMode] = useState('week');
  const [anchorDate, setAnchorDate] = useState(() => new Date());
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [rosterSession, setRosterSession] = useState(null);

  const range = getRangeForMode(mode, anchorDate);
  const { from, to } = range;
  const isCurrent = from === getRangeForMode(mode, new Date()).from;

  const shift = (dir) => setAnchorDate(d => shiftAnchor(mode, d, dir));
  const goToDay = (date) => { setAnchorDate(date); setMode('day'); };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    api.get(`/trainer/schedule?from_date=${from}&to_date=${to}`)
      .then(r => { if (!cancelled) setRows(r.data.data || []); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [from, to]);

  return (
    <>
      <div className="trn-topbar">
        <div className="trn-topbar-title">Schedules</div>
        <div className="trn-topbar-sub">Your weekly class schedule across all batches</div>
      </div>
      <div className="trn-page">
        <div className="trn-card">
          <div className="trn-card-head">
            <h3 className="d-flex align-items-center gap-2">
              <SectionIcon icon="bi-calendar3" color="var(--sms-stat-blue)" />
              {rangeLabel(mode, anchorDate, range)}
            </h3>
            <div className="d-flex gap-2 align-items-center flex-wrap">
              <ViewModeToggle mode={mode} onChange={setMode} />
              <input
                type="date"
                className="sms-form-control"
                style={{ width: 'auto' }}
                value={ymd(anchorDate)}
                onChange={e => { if (e.target.value) setAnchorDate(new Date(`${e.target.value}T00:00:00`)); }}
                aria-label="Jump to date"
              />
              <button type="button" className="sms-btn sms-btn-outline" onClick={() => shift(-1)}>
                <i className="bi bi-chevron-left" /> Previous
              </button>
              <button type="button" className="sms-btn sms-btn-outline" onClick={() => setAnchorDate(new Date())} disabled={isCurrent}>
                {TODAY_LABEL[mode]}
              </button>
              <button type="button" className="sms-btn sms-btn-outline" onClick={() => shift(1)}>
                Next <i className="bi bi-chevron-right" />
              </button>
            </div>
          </div>
          <div className="trn-card-body">
            {mode === 'month' ? (
              <MonthGrid
                anchorDate={anchorDate}
                sessions={rows}
                loading={loading}
                onDayClick={goToDay}
                onSessionClick={setRosterSession}
                showTrainer={false}
              />
            ) : (
              <ScheduleCardGrid
                columns={scheduleColumns(mode, anchorDate)}
                sessions={rows}
                loading={loading}
                onSessionClick={setRosterSession}
                showTrainer={false}
              />
            )}
          </div>
        </div>
      </div>
      {rosterSession && (
        <RosterModal
          session={rosterSession}
          rosterUrl={`/trainer/schedule/${rosterSession.id}/roster`}
          onClose={() => setRosterSession(null)}
        />
      )}
    </>
  );
}
